import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { submitLead, formatApiError } from "@/lib/api";

const INDUSTRIES = ["Restaurant / Cafe", "Retail", "Salon / Services", "Healthcare", "E-commerce", "Other"];
const VOLUMES = ["Just getting started", "Under $10k / month", "$10k to $50k / month", "$50k to $250k / month", "$250k+ / month"];
const EQUIP = ["Countertop terminal", "Mobile / tap reader", "Full POS system", "Online payments", "Not sure yet"];
const EMPTY = { name: "", email: "", phone: "", business: "", industry: "", volume: "", equipment: "" };

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-[background-color,border-color,color,transform] duration-200 active:scale-95 ${active ? "border-lime-400 bg-lime-400 text-[#050B1F]" : "border-emerald-900/15 bg-white text-emerald-950 hover:border-emerald-600"}`}>
    {children}
  </button>
);

export const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-800">{label}</span>
    <input data-testid={testid} {...props}
      className="w-full rounded-xl border border-emerald-900/15 bg-white px-4 py-3 text-[15px] text-emerald-950 outline-none transition-[border-color,box-shadow] placeholder:text-emerald-900/40 focus:border-emerald-600 focus:ring-4 focus:ring-lime-300/40" />
  </label>
);

export const QuoteModal = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setStep(0); setForm(EMPTY); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitLead({ ...form, source: "quote" });
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    <div key="0" className="space-y-6">
      <div>
        <p className="mb-3 text-sm font-semibold text-emerald-950">What kind of business do you run?</p>
        <div className="flex flex-wrap gap-2">{INDUSTRIES.map((x, i) => <Chip key={x} testid={`quote-industry-${i}`} active={form.industry === x} onClick={() => set("industry")(x)}>{x}</Chip>)}</div>
      </div>
      <div>
        <p className="mb-3 text-sm font-semibold text-emerald-950">Roughly how much do you process?</p>
        <div className="flex flex-wrap gap-2">{VOLUMES.map((x, i) => <Chip key={x} testid={`quote-volume-${i}`} active={form.volume === x} onClick={() => set("volume")(x)}>{x}</Chip>)}</div>
      </div>
    </div>,
    <div key="1">
      <p className="mb-3 text-sm font-semibold text-emerald-950">What equipment would help you most?</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {EQUIP.map((x, i) => (
          <button type="button" key={x} data-testid={`quote-equipment-${i}`} onClick={() => set("equipment")(x)}
            className={`flex items-center justify-between rounded-2xl border p-4 text-left text-sm font-semibold transition-[background-color,border-color] ${form.equipment === x ? "border-emerald-700 bg-emerald-50" : "border-emerald-900/15 hover:border-emerald-600"}`}>
            {x}
            <span className={`grid h-5 w-5 place-items-center rounded-full ${form.equipment === x ? "bg-lime-400" : "border border-emerald-900/20"}`}>{form.equipment === x && <Check className="h-3 w-3" />}</span>
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-emerald-900/70">Free equipment is available for qualifying merchants.</p>
    </div>,
    <div key="2" className="grid gap-4 sm:grid-cols-2">
      <Field label="Your name" testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Doe" />
      <Field label="Business name" testid="quote-business-input" value={form.business} onChange={set("business")} placeholder="Main Street Cafe" />
      <Field label="Email" testid="quote-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@business.com" />
      <Field label="Phone" testid="quote-phone-input" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
    </div>,
  ];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-lenis-prevent data-testid="quote-modal" className="max-h-[92vh] w-[calc(100%-1.5rem)] max-w-2xl overflow-y-auto rounded-3xl border-emerald-900/10 p-0 sm:rounded-3xl">
        <div className="relative overflow-hidden bg-[#0B2A6F] px-6 pb-6 pt-8 text-white sm:px-8 grain">
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-lime-400/30 blur-3xl" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-lime-300">Free quote · 60 seconds</span>
          <DialogTitle className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            {step === 3 ? "You're all set." : "Let's cut your fees."}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-emerald-100/85">
            {step === 3 ? "Your dedicated account manager will reach out shortly." : "Tell us a little about your business. No obligation."}
          </DialogDescription>
          {step < 3 && (
            <div className="mt-6 flex gap-2">{[0, 1, 2].map((i) => <div key={i} className="h-1 flex-1 overflow-hidden rounded-full bg-white/15"><motion.div className="h-full bg-lime-400" initial={false} animate={{ width: step >= i ? "100%" : "0%" }} transition={{ duration: 0.5 }} /></div>)}</div>
          )}
        </div>
        {step === 3 ? (
          <div className="px-6 py-10 text-center sm:px-8" data-testid="quote-success">
            <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-lime-400">
              <Check className="h-10 w-10 text-[#050B1F]" strokeWidth={3} />
            </motion.div>
            <p className="mx-auto mt-6 max-w-sm text-emerald-900/80">Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}! We'll review your details and get back to you at <strong>{form.email}</strong>.</p>
            <button onClick={() => close(false)} data-testid="quote-done-btn" className="mt-8 rounded-full bg-[#0B2A6F] px-8 py-3 text-sm font-bold text-white hover:bg-[#050B1F]">Done</button>
          </div>
        ) : (
          <form onSubmit={step === 2 ? submit : (e) => { e.preventDefault(); setStep(step + 1); }} className="px-6 py-7 sm:px-8">
            <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}>{steps[step]}</motion.div>
            <div className="mt-8 flex items-center justify-between">
              <button type="button" onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="quote-back-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-900 disabled:opacity-0">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
              <button type="submit" disabled={busy} data-testid={step === 2 ? "quote-submit-btn" : "quote-next-btn"} className="group inline-flex items-center gap-2 rounded-full bg-[#0B2A6F] px-7 py-3 text-sm font-bold text-white transition-[background-color,transform] hover:bg-[#050B1F] active:scale-95 disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {step === 2 ? "Get my free quote" : "Continue"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
